import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { PersonalAssetWithValueMobxDto } from '../../../../../models/mobx/dtos/personal-assets/personal-asset-with-value-mobx-dto';
import { ValueAmount } from '../../../value/amount/value-amount';
import { Tooltip } from '../../../tooltip/tooltip';

type AssetMiniPersonalType = {
    data: PersonalAssetWithValueMobxDto;
    portfolioCurrencyCode: string;
    showConvertedValue: boolean;
    currencyRatesBySymbol: Record<string, number>;
    onToggleValueDisplay?: () => void;
};

export const AssetMiniPersonal: React.FC<AssetMiniPersonalType> = observer(
    ({ data, portfolioCurrencyCode, showConvertedValue, currencyRatesBySymbol, onToggleValueDisplay }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const isForeignCurrency = data.currencyCode !== portfolioCurrencyCode;
        const currencySymbol = `${data.currencyCode}${portfolioCurrencyCode}`;
        const conversionRate = currencyRatesBySymbol[currencySymbol];

        const canConvert = !isForeignCurrency || conversionRate !== undefined;
        const useConvertedValue = showConvertedValue && canConvert;

        const value = useConvertedValue
            ? (data.value ?? 0) * (isForeignCurrency ? conversionRate : 1)
            : data.value ?? 0;
        const currency = useConvertedValue ? portfolioCurrencyCode : data.currencyCode;
        const canToggleValueDisplay = Boolean(onToggleValueDisplay && isForeignCurrency && canConvert);

        const valueContent = (
            <>
                {useConvertedValue && isForeignCurrency ? '≈ ' : ''}
                <ValueAmount amount={value} nullValue={0} currency={currency} mode="pure" hidable />
            </>
        );

        return (
            <div className="flex items-center justify-between gap-xsmall">
                <div className="flex items-center gap-[0.8rem]">
                    <i
                        className="relative inline-flex h-[1.6rem] w-[1.6rem] items-center justify-center rounded-[30rem] border-[0.2rem] transition-none"
                        style={{ borderColor: theme.iconPortfolio.variant[data.colorName].base.border }}
                    />
                    <div className="max-w-[15rem] overflow-hidden text-ellipsis whitespace-nowrap text-small font-semibold opacity-80">
                        {data.name}
                    </div>
                </div>
                <strong className="text-small">
                    {canToggleValueDisplay ? (
                        <Tooltip content={t('layout.forms.asset.personal.toggleDisplayTooltip')} position="top-right">
                            <button
                                type="button"
                                onClick={onToggleValueDisplay}
                                className="[all:unset] inline-flex cursor-pointer items-center gap-[0.3rem] rounded-[0.4rem] hover:opacity-85 hover:underline hover:decoration-dotted hover:underline-offset-[0.2rem] focus-visible:outline focus-visible:outline-[0.2rem] focus-visible:outline-offset-[0.1rem] focus-visible:outline-border"
                            >
                                {valueContent}
                            </button>
                        </Tooltip>
                    ) : (
                        valueContent
                    )}
                </strong>
            </div>
        );
    },
);
